import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Typography from '@mui/material/Typography'
import Markdown from '../Markdown'
import { bodyText, colours } from '../player/playerStyles'
import Feedback from './Feedback'
import useBlockState from './useBlockState'

// Sorting items into categories with a radio group per item: no dragging,
// so it works the same with a mouse, a keyboard, touch or a screen reader.
const Match = ({ block, initialState, onStateChange }) => {
  const [state, update] = useBlockState({ placements: {}, ...initialState }, onStateChange)
  const placements = state.placements ?? {}
  const placed = block.items.filter((item) => placements[item.id]).length
  const labelOf = (categoryId) => block.categories.find((category) => category.id === categoryId)?.label

  const place = (itemId, categoryId) => update({ ...state, placements: { ...placements, [itemId]: categoryId } })

  return (
    <Box sx={{ maxWidth: '74ch' }}>
      <Markdown sx={{ ...bodyText, mb: 1.5 }}>{block.prompt}</Markdown>
      <Typography aria-live="polite" sx={{ fontSize: '1rem', fontWeight: 700, color: colours.muted, mb: 1.5 }}>
        {placed} of {block.items.length} placed
      </Typography>

      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {block.items.map((item, index) => {
          const chosen = placements[item.id]
          const correct = chosen === item.category
          return (
            <Box component="li" key={item.id} sx={{ mb: 2, p: { xs: 1.5, sm: 2 }, border: `1px solid ${colours.border}`, borderRadius: 1.5 }}>
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormLabel
                  component="legend"
                  sx={{ fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.5, color: `${colours.text} !important`, mb: 0.5, p: 0 }}
                >
                  {index + 1}. {item.text}
                </FormLabel>
                <RadioGroup
                  name={`${block.id}-${item.id}`}
                  value={chosen ?? ''}
                  onChange={(event) => place(item.id, event.target.value)}
                  sx={{ flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', columnGap: 1 }}
                >
                  {block.categories.map((category) => (
                    <FormControlLabel
                      key={category.id}
                      value={category.id}
                      control={<Radio sx={{ color: colours.border, '&.Mui-checked': { color: colours.accent } }} />}
                      label={category.label}
                      sx={{ minHeight: 44, mr: 2, '& .MuiFormControlLabel-label': { fontSize: '1.0625rem', lineHeight: 1.4 } }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              <Box aria-live="polite">
                {chosen && (
                  <Feedback
                    tone={correct ? 'good' : 'bad'}
                    label={
                      correct
                        ? `Correct: ${labelOf(chosen)}`
                        : `Not quite: this does not belong in ${labelOf(chosen)}. You can choose again.`
                    }
                    sx={{ mt: 1 }}
                  >
                    {item.feedback}
                  </Feedback>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default Match
